import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, DollarSign, UserPlus, Trash2, QrCode, Copy, Eye, RefreshCw,
  CreditCard,
} from "lucide-react";
import { format } from "date-fns";
import { QRCodeSVG } from "qrcode.react";
const QRCode = QRCodeSVG;

type Status = "submitted" | "approved" | "paid" | "provisioned" | "rejected";

interface Application {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  photo_url: string | null;
  company: string;
  title: string;
  category: string;
  chapter: "washington" | "union" | "all";
  description: string;
  about: string | null;
  website: string | null;
  linkedin: string | null;
  facebook: string | null;
  reference_1_name: string;
  reference_1_phone: string;
  reference_1_email: string;
  reference_2_name: string;
  reference_2_phone: string;
  reference_2_email: string;
  reference_3_name: string;
  reference_3_phone: string;
  reference_3_email: string;
  heard_about: string;
  community_organizations: string | null;
  volunteer_activities: string | null;
  reason_to_join: string;
  business_goals: string;
  agreed_conduct: boolean;
  agreed_attendance: boolean;
  agreed_background_check: boolean;
  status: Status;
  admin_notes: string | null;
  rejection_reason: string | null;
  approved_at: string | null;
  paid_at: string | null;
  provisioned_at: string | null;
  member_id: string | null;
  created_at: string;
}

const statusBadge: Record<Status, string> = {
  submitted: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  approved: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  paid: "bg-purple-500/15 text-purple-700 dark:text-purple-300",
  provisioned: "bg-green-500/15 text-green-700 dark:text-green-300",
  rejected: "bg-red-500/15 text-red-700 dark:text-red-300",
};

const AdminApplicationList = () => {
  const [statusFilter, setStatusFilter] = useState<Status | "all">("all");
  const [chapterFilter, setChapterFilter] = useState<"all" | "washington" | "union">("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: apps, isLoading, refetch } = useQuery({
    queryKey: ["applications"],
    queryFn: async (): Promise<Application[]> => {
      const { data, error } = await supabase
        .from("applications")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as any) ?? [];
    },
  });

  const filtered = useMemo(() => {
    return (apps ?? []).filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (chapterFilter !== "all" && a.chapter !== chapterFilter) return false;
      return true;
    });
  }, [apps, statusFilter, chapterFilter]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { submitted: 0, approved: 0, paid: 0, provisioned: 0, rejected: 0 };
    (apps ?? []).forEach((a) => { c[a.status] = (c[a.status] ?? 0) + 1; });
    return c;
  }, [apps]);

  const applyUrl = `${window.location.origin}/apply`;

  const openApp = openId ? (apps ?? []).find((a) => a.id === openId) ?? null : null;

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {(["submitted", "approved", "paid", "provisioned", "rejected"] as Status[]).map((s) => (
          <Card key={s}>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{s}</div>
              <div className="text-2xl font-bold mt-1">{counts[s] ?? 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Applications Pipeline</CardTitle>
              <CardDescription>Review applicants and provision approved members.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <QrCode className="h-4 w-4 mr-1" /> Share /apply
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Share the application link</DialogTitle>
                    <DialogDescription>
                      Anyone with this link or QR code can submit an application.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex flex-col items-center gap-4 py-4">
                    <div className="bg-white p-4 rounded">
                      <QRCode value={applyUrl} size={200} />
                    </div>
                    <div className="flex w-full gap-2">
                      <Input readOnly value={applyUrl} />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          navigator.clipboard.writeText(applyUrl);
                          toast.success("Link copied");
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="provisioned">Provisioned</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Chapter</Label>
              <Select value={chapterFilter} onValueChange={(v: any) => setChapterFilter(v)}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="washington">Washington</SelectItem>
                  <SelectItem value="union">Union</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No applications match the current filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Applicant</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Chapter</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>
                        <div className="font-medium">{a.first_name} {a.last_name}</div>
                        <div className="text-xs text-muted-foreground">{a.email}</div>
                      </TableCell>
                      <TableCell>
                        <div>{a.company}</div>
                        <div className="text-xs text-muted-foreground">{a.title}</div>
                      </TableCell>
                      <TableCell className="capitalize">{a.chapter}</TableCell>
                      <TableCell>
                        <Badge className={statusBadge[a.status]} variant="secondary">
                          {a.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(a.created_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => setOpenId(a.id)}>
                          <Eye className="h-4 w-4 mr-1" /> Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ApplicationDetailDialog
        application={openApp}
        open={!!openApp}
        onClose={() => setOpenId(null)}
        onChanged={() => refetch()}
      />
    </div>
  );
};

// Admin-only: shows which reviewer approved / rejected / re-opened.
// Never passed to emails or applicant-facing pages.
const ReviewHistory = ({ a }: { a: any }) => {
  const ids = [a.approved_by, a.rejected_by, a.reopened_by].filter(Boolean) as string[];
  const { data: names = {} } = useQuery({
    queryKey: ["reviewer-names", ids.join(",")],
    enabled: ids.length > 0,
    queryFn: async () => {
      const map: Record<string, string> = {};
      const { data: m } = await supabase.from("members").select("user_id, name").in("user_id", ids);
      (m || []).forEach((r: any) => { if (r.user_id) map[r.user_id] = r.name; });
      const missing = ids.filter((i) => !map[i]);
      if (missing.length) {
        const { data: p } = await supabase.from("profiles").select("id, email").in("id", missing);
        (p || []).forEach((r: any) => { map[r.id] = r.email; });
      }
      return map;
    },
  });
  const who = (id: string | null) => (id ? names[id] ?? "…" : "Unknown");
  const when = (t: string | null) => (t ? ` on ${format(new Date(t), "MMM d, h:mm a")}` : "");
  const lines: string[] = [];
  if (a.approved_at || a.approved_by) lines.push(`Approved by ${who(a.approved_by)}${when(a.approved_at)}`);
  if (a.status === "rejected") lines.push(`Rejected by ${who(a.rejected_by)}${when(a.rejected_at)}`);
  if (a.reopened_by) lines.push(`Re-opened by ${who(a.reopened_by)}${when(a.reopened_at)}`);
  if (!lines.length) return null;
  return (
    <div className="rounded border p-3 text-xs text-muted-foreground space-y-1">
      <div className="font-semibold text-foreground">Review history (admins only)</div>
      {lines.map((l) => <div key={l}>{l}</div>)}
    </div>
  );
};

const ApplicationDetailDialog = ({
  application, open, onClose, onChanged,
}: {
  application: Application | null;
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}) => {
  const [notes, setNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [photoSignedUrl, setPhotoSignedUrl] = useState<string>("");

  // Sync notes when application changes
  useMemo(() => {
    setNotes(application?.admin_notes ?? "");
    setRejectionReason(application?.rejection_reason ?? "");
  }, [application?.id]);

  // Fetch a signed URL for the applicant photo (bucket is private).
  useMemo(() => {
    setPhotoSignedUrl("");
    const raw = application?.photo_url;
    if (!raw) return;
    // Extract path: legacy values may be full public URLs, new ones are bare paths.
    const marker = "/application-photos/";
    const idx = raw.indexOf(marker);
    const path = idx >= 0 ? raw.slice(idx + marker.length) : raw;
    supabase.storage
      .from("application-photos")
      .createSignedUrl(path, 600)
      .then(({ data }) => {
        if (data?.signedUrl) setPhotoSignedUrl(data.signedUrl);
      });
  }, [application?.id, application?.photo_url]);

  if (!application) return null;
  const a = application;

  const update = async (patch: Partial<Application>, action: string) => {
    setBusy(action);
    try {
      const { error } = await supabase.from("applications").update(patch).eq("id", a.id);
      if (error) throw error;
      toast.success("Updated");
      onChanged();
    } catch (err: any) {
      toast.error(err?.message ?? "Update failed");
    } finally {
      setBusy(null);
    }
  };

  // Returns null on success, or a human-readable failure reason.
  const invokeEmail = async (body: Record<string, unknown>): Promise<string | null> => {
    const { data, error } = await supabase.functions.invoke("send-app-email", { body });
    if (error) {
      let detail = "";
      try {
        detail = (await (error as any)?.context?.json?.())?.error ?? "";
      } catch {
        detail = "";
      }
      return detail || error.message || "Email failed to send";
    }
    if ((data as any)?.reason === "email_suppressed") {
      return "this address is blocked (bounced or unsubscribed)";
    }
    if ((data as any)?.success === false || (data as any)?.error) {
      return (data as any)?.error ?? "Email failed to send";
    }
    return null;
  };

  const sendApprovedEmail = (paymentUrl: string) =>
    invokeEmail({
      templateName: "application-approved",
      recipientEmail: a.email,
      idempotencyKey: `app-approved-${a.id}-${Date.now()}`,
      templateData: {
        firstName: a.first_name,
        chapter: a.chapter === "washington" ? "Washington" : a.chapter === "union" ? "Union" : "RCN",
        paymentUrl,
      },
    });

  const handleApprove = async () => {
    setBusy("approve");
    const previousStatus = a.status;
    let paymentUrl: string | undefined;
    try {
      // Mark the application approved FIRST: the payment link can only be
      // created for an approved application.
      const { error: updErr } = await supabase
        .from("applications")
        .update({
          status: "approved",
          approved_at: new Date().toISOString(),
          approved_by: (await supabase.auth.getUser()).data.user?.id ?? null,
          admin_notes: notes || null,
        })
        .eq("id", a.id);
      if (updErr) throw updErr;

      const { data, error } = await supabase.functions.invoke("create-membership-checkout", {
        body: {
          application_id: a.id,
          mode: "new_member",
          success_url: `${window.location.origin}/auth`,
          cancel_url: `${window.location.origin}/apply`,
        },
      });
      if (error) {
        let detail = "";
        try {
          detail = (await (error as any)?.context?.json?.())?.error ?? "";
        } catch {
          detail = "";
        }
        throw new Error(detail || error.message);
      }
      if ((data as any)?.error) throw new Error((data as any).error);
      paymentUrl = (data as any)?.url;
      if (!paymentUrl) throw new Error("No payment URL returned from Stripe");
    } catch (err: any) {
      // Roll the status back so the application can be approved again cleanly.
      await supabase
        .from("applications")
        .update({ status: previousStatus, approved_at: null, approved_by: null } as any)
        .eq("id", a.id);
      toast.error(err?.message ?? "Approval failed — please retry");
      onChanged();
      setBusy(null);
      return;
    }

    // Application stays approved; report the email outcome honestly.
    const emailErr = await sendApprovedEmail(`https://rcnmo.com/pay-dues?app=${a.id}`);
    if (emailErr) {
      toast.error(`Approved, but the email was not delivered: ${emailErr}. Use "Send payment link" to retry.`);
    } else {
      toast.success("Approval email sent with payment link");
    }
    onChanged();
    setBusy(null);
  };

  const handleSendPaymentLink = async () => {
    setBusy("payment-link");
    try {
      const { data, error } = await supabase.functions.invoke("create-membership-checkout", {
        body: {
          application_id: a.id,
          mode: "new_member",
          success_url: `${window.location.origin}/auth`,
          cancel_url: `${window.location.origin}/apply`,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      const paymentUrl = (data as any)?.url;
      if (!paymentUrl) throw new Error("No payment URL returned from Stripe");
      const emailErr = await invokeEmail({
        templateName: "payment-link-applicant",
        recipientEmail: a.email,
        idempotencyKey: `payment-link-${a.id}-${Date.now()}`,
        templateData: {
          firstName: a.first_name,
          chapter: a.chapter === "washington" ? "Washington" : a.chapter === "union" ? "Union" : "RCN",
          paymentUrl: `https://rcnmo.com/pay-dues?app=${a.id}`,
          amountLabel: "$300/year",
        },
      });
      if (emailErr) throw new Error(`Payment link email was not delivered: ${emailErr}`);
      toast.success("Payment link sent to applicant");
      onChanged();
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to send payment link");
    } finally {
      setBusy(null);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    await update({
      status: "rejected",
      rejection_reason: rejectionReason,
      admin_notes: notes || null,
      rejected_by: user?.id ?? null,
      rejected_at: new Date().toISOString(),
    } as any, "reject");
  };

  const handleReopen = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    await update({
      status: "submitted",
      rejection_reason: null,
      approved_at: null,
      reopened_by: user?.id ?? null,
      reopened_at: new Date().toISOString(),
    } as any, "reopen");
  };

  const handleMarkPaid = async () => {
    await update({ status: "paid", paid_at: new Date().toISOString() }, "paid");
  };

  const handleProvision = async () => {
    setBusy("provision");
    try {
      const { data, error } = await supabase.functions.invoke("provision-member-from-application", {
        body: {
          application_id: a.id,
          login_url: `${window.location.origin}/auth`,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success("Member account provisioned and welcome email sent");
      onChanged();
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? "Provisioning failed");
    } finally {
      setBusy(null);
    }
  };

  const handleResendWelcome = async () => {
    setBusy("resend");
    try {
      await supabase.functions.invoke("send-app-email", {
        body: {
          templateName: "application-welcome",
          recipientEmail: a.email,
          idempotencyKey: `app-welcome-resend-${a.id}-${Date.now()}`,
          templateData: {
            firstName: a.first_name,
            email: a.email,
            tempPassword: "(use Reset Password from /auth)",
            loginUrl: `${window.location.origin}/auth`,
          },
        },
      });
      toast.success("Welcome email resent");
    } catch (err: any) {
      toast.error(err?.message ?? "Resend failed");
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    setBusy("delete");
    try {
      const { error } = await supabase.from("applications").delete().eq("id", a.id);
      if (error) throw error;
      toast.success("Application deleted");
      onChanged();
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {a.first_name} {a.last_name}
            <Badge className={statusBadge[a.status]} variant="secondary">{a.status}</Badge>
          </DialogTitle>
          <DialogDescription>
            Submitted {format(new Date(a.created_at), "MMMM d, yyyy")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header section */}
          <div className="flex flex-col sm:flex-row gap-4">
            {photoSignedUrl && (
              <img src={photoSignedUrl} alt="" className="w-28 h-28 object-cover rounded-lg border" />
            )}
            <div className="flex-1 space-y-1 text-sm">
              <div><strong>Company:</strong> {a.company} — {a.title}</div>
              <div><strong>Category:</strong> {a.category}</div>
              <div><strong>Chapter:</strong> <span className="capitalize">{a.chapter}</span></div>
              <div><strong>Email:</strong> <a className="text-primary" href={`mailto:${a.email}`}>{a.email}</a></div>
              <div><strong>Phone:</strong> <a className="text-primary" href={`tel:${a.phone}`}>{a.phone}</a></div>
              {a.website && <div><strong>Website:</strong> <a className="text-primary" href={a.website} target="_blank" rel="noreferrer">{a.website}</a></div>}
            </div>
          </div>

          <Section title="Description">{a.description}</Section>
          {a.about && <Section title="About the business">{a.about}</Section>}

          <Section title="Why they want to join">{a.reason_to_join}</Section>
          <Section title="Business goals">{a.business_goals}</Section>
          <Section title="How they heard about RCN">{a.heard_about}</Section>

          {a.community_organizations && (
            <Section title="Community organizations">{a.community_organizations}</Section>
          )}
          {a.volunteer_activities && (
            <Section title="Volunteer activities">{a.volunteer_activities}</Section>
          )}

          {/* References */}
          <div>
            <h3 className="font-semibold mb-2">References</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="bg-muted/40">
                  <CardContent className="p-3 text-sm space-y-0.5">
                    <div className="font-medium">{(a as any)[`reference_${i}_name`]}</div>
                    <div className="text-muted-foreground">{(a as any)[`reference_${i}_phone`]}</div>
                    <div className="text-muted-foreground break-all">{(a as any)[`reference_${i}_email`]}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Agreements */}
          <div>
            <h3 className="font-semibold mb-2">Agreements</h3>
            <ul className="text-sm space-y-1">
              <li>{a.agreed_conduct ? "✅" : "❌"} Conduct on social/website</li>
              <li>{a.agreed_attendance ? "✅" : "❌"} Attendance & substitution policy</li>
              <li>{a.agreed_background_check ? "✅" : "❌"} Background screening consent</li>
            </ul>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="admin-notes">Admin notes (private)</Label>
            <Textarea id="admin-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            <Button
              size="sm"
              variant="outline"
              onClick={() => update({ admin_notes: notes || null }, "notes")}
              disabled={busy === "notes"}
            >
              Save notes
            </Button>
          </div>

          {/* Rejection reason (only relevant for reject) */}
          {a.status !== "rejected" && a.status !== "provisioned" && (
            <div className="space-y-2">
              <Label htmlFor="reject-reason">Rejection reason (only used if rejecting)</Label>
              <Textarea id="reject-reason" rows={2} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} />
            </div>
          )}
          {a.rejection_reason && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-3 text-sm">
              <strong>Rejection reason:</strong> {a.rejection_reason}
            </div>
          )}
          <ReviewHistory a={a} />
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="text-destructive mr-auto" disabled={!!busy}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this application?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the application record. The provisioned member account (if any) is NOT deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {a.status === "rejected" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={!!busy}>
                  <RefreshCw className="h-4 w-4 mr-1" /> Re-open for review
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Re-open this application?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {a.first_name} {a.last_name}'s application will go back to "Submitted" and the rejection reason will be cleared. The applicant is not emailed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleReopen}>Yes, re-open</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {a.status === "submitted" && (
            <>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={!!busy}
                    onClick={(e) => {
                      if (!rejectionReason.trim()) {
                        e.preventDefault();
                        toast.error("Please provide a rejection reason");
                      }
                    }}
                  >
                    <XCircle className="h-4 w-4 mr-1" /> Reject
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reject {a.first_name} {a.last_name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Reason: "{rejectionReason}"
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleReject}>Yes, reject</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button onClick={handleApprove} disabled={!!busy}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve & email
              </Button>
            </>
          )}
          {a.status === "approved" && (
            <>
              <Button variant="outline" onClick={handleMarkPaid} disabled={!!busy}>
                <DollarSign className="h-4 w-4 mr-1" /> Mark Paid
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(`https://rcnmo.com/pay-dues?app=${a.id}`);
                  toast.success("Payment link copied — it never expires");
                }}
              >
                <Copy className="h-4 w-4 mr-1" /> Copy payment link
              </Button>
              <Button onClick={handleSendPaymentLink} disabled={!!busy}>
                <CreditCard className="h-4 w-4 mr-1" />
                {busy === "payment-link" ? "Sending..." : "Send payment link"}
              </Button>
            </>
          )}
          {a.status === "paid" && (
            <Button onClick={handleProvision} disabled={!!busy}>
              <UserPlus className="h-4 w-4 mr-1" />
              {busy === "provision" ? "Provisioning..." : "Provision member account"}
            </Button>
          )}
          {a.status === "provisioned" && (
            <Button variant="outline" onClick={handleResendWelcome} disabled={!!busy}>
              {busy === "resend" ? "Sending..." : "Resend welcome email"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <h3 className="font-semibold mb-1">{title}</h3>
    <p className="text-sm text-muted-foreground whitespace-pre-line">{children}</p>
  </div>
);

export default AdminApplicationList;
